import { buildDomainSeoBundle } from "./agenticSeoUtils";

export const INSURANCE_DOMAIN_INTRO = {
  title: "Insurance",
  content: "Agentic AI workflow automation for Insurance — across Commercial Insurance, Insurance Compliance, Customer Experience, and Legal Claims. Use cases span underwriting submission summarization, suspicious claim signal analysis, complaint handling with SLA tracking, and litigation case summarization. Practical deployments for insurers and brokers in India, UAE, KSA, UK, and USA with human-in-the-loop review, status tracking, and audit evidence.",
  seoDescription: "Insurance agentic AI use cases — commercial insurance, insurance compliance, customer experience, legal claims. Underwriting submission summarization, suspicious claim signal summarization, complaint handling SLA tracking, litigation case summary. Policy admin claims CRM integration.",
};

const slug = (label) => label.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

function withMeta(uc) { return { ...uc, groupId: slug(uc.subIndustry), seoTitle: uc.title, title: uc.shortTitle || uc.title }; }

const INSURANCE_SYSTEMS = "Policy Admin System, Claims System, CRM, Document Management, Email, Power BI";

const RAW = [
  {
    id: "underwriting-submission-summarization",
    subIndustry: "Commercial Insurance",
    department: "Underwriting",
    businessProcess: "Underwriting submission summarization",
    regions: "India, UAE/Dubai, KSA/Saudi Arabia, UK, USA",
    shortTitle: "Underwriting submission summarization",
    title: "Underwriting Submission Summarization Automation for Insurance",
    seoKeywords: "insurance, underwriting submission summarization, AI automation, RPA, agentic AI, document AI",
    challenge: "Underwriters spend time reviewing long broker submissions, schedules, loss runs and questionnaires.",
    solution: "Implement Agentic AI + Document AI to capture inputs, validate rules, route exceptions, generate summaries or actions, and update the target systems with human approval where required.",
    summary: "Commercial Insurance teams can improve underwriting submission summarization with agentic AI workflow automation that captures inputs, validates rules, routes exceptions, and updates business systems with human approval where needed — reducing manual effort and improving audit trails.",
    automationType: "Agentic AI + Document AI",
    buyerPersona: "Chief Underwriting Officer, CIO",
    systems: INSURANCE_SYSTEMS,
    agentsRequired: "Document Extraction Agent, Underwriting Validation Agent, Exception Agent",
    inputData: "Forms, PDFs, emails, scanned documents, attachments, master data and underwriting submission summarization rules",
    outputActions: "Extracted structured data, validation results, exception list and approval-ready underwriting submission summarization record",
    keyBenefits: "Faster Turnaround, Lower Manual Effort, Improved Accuracy And Better Operational Visibility",
    roiTimeline: "12+ months",
    implementationComplexity: "High",
    businessImpact: "High",
  },
  {
    id: "suspicious-claim-signal-summarization",
    subIndustry: "Insurance Compliance",
    department: "Risk and Fraud",
    businessProcess: "Suspicious claim signal summarization",
    regions: "India, UAE/Dubai, KSA/Saudi Arabia, UK, USA",
    shortTitle: "Suspicious claim signal summarization",
    title: "Suspicious Claim Signal Summarization Automation for Insurance",
    seoKeywords: "insurance, suspicious claim signal summarization, AI automation, RPA, agentic AI",
    challenge: "Analysts manually inspect claim patterns, document inconsistencies and prior loss history.",
    solution: "Implement Agentic AI + Data Automation to capture inputs, validate rules, route exceptions, generate summaries or actions, and update the target systems with human approval where required.",
    summary: "Insurance Compliance teams can improve suspicious claim signal summarization with agentic AI workflow automation that captures inputs, validates rules, routes exceptions, and updates business systems with human approval where needed — reducing manual effort and improving audit trails.",
    automationType: "Agentic AI + Data Automation",
    buyerPersona: "Risk Head, Compliance Head",
    systems: INSURANCE_SYSTEMS,
    agentsRequired: "Data Collection Agent, Suspicious Insight Agent, Alert Agent",
    inputData: "System exports, transaction records, logs, historical data, master data and suspicious claim signal summarization business rules",
    outputActions: "Dashboard, anomaly alerts, forecast or action list for suspicious claim signal summarization",
    keyBenefits: "Stronger Controls, Faster Review, Clearer Audit Trail And Fewer Missed Exceptions",
    roiTimeline: "12+ months",
    implementationComplexity: "High",
    businessImpact: "High",
  },
  {
    id: "litigation-case-summarization",
    subIndustry: "Legal Claims",
    department: "Litigation",
    businessProcess: "Litigation case summarization",
    regions: "India, UAE/Dubai, KSA/Saudi Arabia, UK, USA",
    shortTitle: "Litigation case summarization",
    title: "Litigation Case Summarization Automation for Insurance",
    seoKeywords: "insurance, litigation case summarization, AI automation, RPA, agentic AI, document AI",
    challenge: "Legal teams manually summarize claim notes, correspondence, court dates and external counsel updates.",
    solution: "Implement Agentic AI + Document AI to capture inputs, validate rules, route exceptions, generate summaries or actions, and update the target systems with human approval where required.",
    summary: "Legal Claims teams can improve litigation case summarization with agentic AI workflow automation that captures inputs, validates rules, routes exceptions, and updates business systems with human approval where needed — reducing manual effort and improving audit trails.",
    automationType: "Agentic AI + Document AI",
    buyerPersona: "Legal Head, Claims Head",
    systems: INSURANCE_SYSTEMS,
    agentsRequired: "Document Extraction Agent, Litigation Validation Agent, Exception Agent",
    inputData: "Forms, PDFs, emails, scanned documents, attachments, master data and litigation case summarization rules",
    outputActions: "Extracted structured data, validation results, exception list and approval-ready litigation case summarization record",
    keyBenefits: "Faster Turnaround, Lower Manual Effort, Improved Accuracy And Better Operational Visibility",
    roiTimeline: "6-12 months",
    implementationComplexity: "High",
    businessImpact: "Medium",
  },
];
export const INSURANCE_USE_CASES = RAW.map(withMeta);
const GROUP_ORDER = ["Commercial Insurance", "Insurance Compliance", "Legal Claims"];
export const INSURANCE_GROUPS = GROUP_ORDER.map((label) => ({ id: slug(label), label })).filter((g) => INSURANCE_USE_CASES.some((uc) => uc.groupId === g.id));
const seo = buildDomainSeoBundle({ domainSlug: 'insurance-domain', domainIntro: INSURANCE_DOMAIN_INTRO, useCases: INSURANCE_USE_CASES });
export const INSURANCE_USE_CASES_SEO = seo.useCasesSeo;
export const INSURANCE_DOMAIN_FAQS = seo.domainFaqs;
export const INSURANCE_ITEM_LIST_JSONLD = seo.itemListJsonLd;
export const INSURANCE_FAQ_JSONLD = seo.faqJsonLd;
export const INSURANCE_BREADCRUMB_JSONLD = seo.breadcrumbJsonLd;
export const INSURANCE_WEBPAGE_JSONLD = seo.webPageJsonLd;

